import React, { useState, useRef, useCallback } from 'react';
import { Camera, Trash2, Loader2, Upload, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

const InitialsAvatar = ({ name, email, size = 96 }) => {
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : email ? email[0].toUpperCase() : '?';

  return (
    <div
      data-testid="initials-avatar"
      className="rounded-full flex items-center justify-center bg-gradient-to-br from-[#B49A88] to-[#B49A88] text-white font-serif font-bold select-none"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
};

const AvatarUpload = ({ user, onAvatarChange }) => {
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileRef = useRef(null);
  const avatarSrc = user?.avatar_url
    ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}?_t=${Date.now()}`
    : null;

  const clearMessages = () => { setError(''); setSuccess(''); };

  const handleFileSelect = useCallback((e) => {
    clearMessages();
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Invalid file format. Only JPG, PNG and WebP are allowed.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('File is too large. Maximum size is 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target.result);
      setPreviewFile(file);
    };
    reader.readAsDataURL(file);
  }, []);

  const cancelPreview = () => {
    setPreview(null);
    setPreviewFile(null);
    clearMessages();
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!previewFile) return;
    setUploading(true);
    clearMessages();

    try {
      const formData = new FormData();
      formData.append('file', previewFile);

      const res = await axios.post(`${API}/avatar/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess(res.data.message || 'Profilbilde updated');
      setPreview(null);
      setPreviewFile(null);
      if (fileRef.current) fileRef.current.value = '';
      onAvatarChange?.(res.data.avatar_url);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not upload image. Please try again.');
    }
    setUploading(false);
  };

  const handleRemove = async () => {
    setRemoving(true);
    clearMessages();
    try {
      await axios.delete(`${API}/avatar/remove`);
      setSuccess('Profilbilde fjernet');
      onAvatarChange?.(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not fjerne bildet.');
    }
    setRemoving(false);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5" data-testid="avatar-upload-section">
      {/* Avatar display */}
      <div className="relative group flex-shrink-0">
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            data-testid="avatar-preview"
            className="w-24 h-24 rounded-full object-cover border-2 border-[#C49A6C]/30"
          />
        ) : avatarSrc ? (
          <img
            src={avatarSrc}
            alt="Profilbilde"
            data-testid="avatar-image"
            className="w-24 h-24 rounded-full object-cover border-2 border-stone-200"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        {!preview && !avatarSrc && (
          <InitialsAvatar name={user?.full_name} email={user?.email} size={96} />
        )}
        {/* Hidden fallback for broken images */}
        {avatarSrc && !preview && (
          <div style={{ display: 'none' }}>
            <InitialsAvatar name={user?.full_name} email={user?.email} size={96} />
          </div>
        )}

        {/* Camera overlay */}
        {!preview && (
          <button
            data-testid="avatar-camera-btn"
            onClick={() => fileRef.current?.click()}
            className="absolute inset-0 rounded-full bg-black/0 hover:bg-black/40 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
          >
            <Camera className="w-6 h-6 text-white" />
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex-1 min-w-0 text-center sm:text-left">
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="avatar-file-input"
        />

        {preview ? (
          <div className="space-y-2">
            <p className="font-sans text-xs text-stone-500">
              Preview — image will be cropped automatically til kvadratisk format.
            </p>
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <Button
                data-testid="avatar-upload-btn"
                onClick={handleUpload}
                disabled={uploading}
                className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-4 h-8 text-xs font-sans font-semibold"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
              <Button
                data-testid="avatar-cancel-btn"
                onClick={cancelPreview}
                variant="ghost"
                disabled={uploading}
                className="text-stone-400 hover:text-stone-600 h-8 px-3 text-xs font-sans"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="font-sans text-xs text-stone-500">
              JPG, PNG or WebP. Max 5 MB.
            </p>
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <Button
                data-testid="avatar-choose-btn"
                onClick={() => fileRef.current?.click()}
                variant="outline"
                className="rounded-full h-8 px-4 text-xs font-sans font-semibold border-stone-200"
              >
                <Camera className="w-3.5 h-3.5 mr-1.5" />
                {avatarSrc ? 'Change photo' : 'Choose photo'}
              </Button>
              {avatarSrc && (
                <Button
                  data-testid="avatar-remove-btn"
                  onClick={handleRemove}
                  variant="ghost"
                  disabled={removing}
                  className="text-stone-400 hover:text-red-500 h-8 px-3 text-xs font-sans"
                >
                  {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                  Remove
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Status messages */}
        {error && (
          <p data-testid="avatar-error" className="font-sans text-xs text-red-500 mt-2">{error}</p>
        )}
        {success && (
          <p data-testid="avatar-success" className="font-sans text-xs text-green-600 mt-2">{success}</p>
        )}
      </div>
    </div>
  );
};

export { InitialsAvatar };
export default AvatarUpload;
